import { useEffect, useState } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  getReportDetail,
  getInvestigationListByReportId,
  catalogByPart,
  listAssignedUsers,
  getOffendersListByReportId,
  getVictimsListByReportId,
  DeleteReport,
  detailAdvisorReview,
} from "../../../apis";
import { Loading } from "../../Loading/String/Loading";
import { Information } from "./Information/Information";
import { Care } from "./Care/Care";
import { InvestigationPage } from "./Investigation2/Main/InvestigationPage";
import { Messages } from "./Messages/Messages";
import { ReportBitacoraTab } from "./Bitacora/ReportBitacoraTab";
import { Users } from "./Users/Users";
import { useTranslation } from "react-i18next";
import { EthicsModal } from "../../Modal/EthicsModal";
import { PDFCreator } from "../../Report/All/pdf/PDFCreator";
import { ReportHeader } from "../../Title/ReportHeader";
import { ReportHeader2 } from "../../Title/ReportHeader2";
import { Box } from "@mui/material";
import { useModalConfirm } from "../../../hooks/useModalConfirm";
import useLocalizedLabel from "../../../hooks/useLocalizedLabel";
import {
  formatWorkdayMessage,
  resolveSeverityLabel,
  useWorkdayCase,
} from "../../../hooks/useWorkdayCase";
import { useReportContext } from "../ReportContext/useReportContext";
import { Confirmation } from "../../Modal/Confirmation/Confirmation";
import { ModalAttachments } from "../../Modal/Attachments/ModalAttachments";
import { ReportEdit } from "./ReportEdit";
import { ReclassificationComponent } from "./Information/ReclassificationReport";
import { useReport } from "../../../context/report/ReportContext";

export const getSafeParsedItem = (key) => {
  try {
    const item = localStorage.getItem(key);
    return JSON.parse(item);
  } catch (e) {
    console.error(`Error parsing JSON from localStorage for key "${key}":`, e);
    return null;
  }
};

export const initialCurrentReportLS = (id) => ({
  id,
  tab: "Información",
  "inv-alias": null,
  "inv-folder": "Datos",
  "inv-id": null,
  "inv-tab": "researchFolder",
});

// Details of raised reports
export const ReportViewer = ({ contentRef, tc }) => {
  const [t] = useTranslation("report");
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { setDetailReport, resetReport } = useReport();
  // En /reports/tc/:id el parámetro de la URL es un tracking code, no el id del
  // reporte, y todos los endpoints de acciones piden report_id.
  const isTrackingCode = tc === true || tc === "true";
  // Id real del reporte: cuando llegamos por tracking code se resuelve con el
  // detalle (que acepta id o tc) antes de disparar el resto de los llamados.
  // Se deriva en cada render y no se guarda routeId en estado: al saltar de una
  // denuncia a otra (buscador, notificaciones) el componente no se desmonta y
  // un useState(routeId) se quedaría con el id de la anterior.
  const [tcReportId, setTcReportId] = useState(null);
  const id = isTrackingCode ? tcReportId : routeId;

  const { closeModalConfirm, modalConfirm, openModalConfirm } =
    useModalConfirm();
  // Obtaining report information from localStorage
  const currentReportLS =
    getSafeParsedItem("current-report") ?? initialCurrentReportLS(id);
  // -----> GENERAL DATA REPORT <----- //
  // Features making up the report
  const [reportDetail, setReportDetail] = useState({});

  // Investigations in selected report
  const [investigationList, setInvestigationList] = useState([]);

  // -----> REPORT VIEW TOOLS <-----//
  // Initial Report Tab
  const [activeTab, setActiveTab] = useState(
    currentReportLS?.tab ?? "Información",
  );
  // Attachments modal
  const [showModalAttachments, setShowModalAttachments] = useState(false);
  // Generate PDFs modal
  const [showModalPDFGenerator, setShowModalPDFGenerator] = useState(false);
  // Generate PDFs modal for investigation
  const [
    showModalPDFGeneratorInvestigation,
    setShowModalPDFGeneratorInvestigation,
  ] = useState(false);
  // Print investigation modal
  const [showModalPrintInvestigation, setShowModalPrintInvestigation] =
    useState(false);
  // A general report loading
  const [isLoading, setIsLoading] = useState(true);

  // --- Catalog Info ---- //
  // Severity level
  const [severityCat, setSeverityCat] = useState([]);
  // Improper Causes
  const [improperCat, setImproperCat] = useState([]);
  // Locking Causes
  const [lockedCat, setLockedCat] = useState([]);
  // Relationship With the Company
  const [companyRelationshipCat, setCompanyRelationshipCat] = useState([]);
  // Grievance Classification
  const [grievanceClassification, setGrievanceClassification] = useState([]);
  // Assigned users
  const [assignedUsers, setAssignedUsers] = useState([]);
  // Edit report
  const [editReport, setEditReport] = useState(false);
  // Reclassification report
  const [reclassificationReport, setReclassificationReport] = useState(false);

  // --- Modals ---- //
  const [showComplementsModal, setShowComplementsModal] = useState(false);
  // Offender people
  const [offenders, setOffenders] = useState([]);
  // Victims people
  const [victims, setVictims] = useState([]);

  const [advisorReview, setAdvisorReview] = useState({});

  const [isLoadingInvestigation, setIsLoadingInvestigation] = useState(false);

  // On mount scene
  useEffect(() => {
    reportRender();
    // cleanup
    return () => {
      resetReport();
    };
  }, [routeId]);

  // Report stored in the localstorage
  useEffect(() => {
    const newCurrentReportLS = { ...currentReportLS, tab: activeTab };
    localStorage.setItem("current-report", JSON.stringify(newCurrentReportLS));
  }, [activeTab]);

  // Cuando cambie el id del reporte que reinicie los datos de current-report en el LS
  // (por tracking code el id llega hasta que se resuelve el detalle)
  useEffect(() => {
    if (!id) return;
    if (id !== currentReportLS?.id) {
      const newCurrentReprotLS = initialCurrentReportLS(id);
      localStorage.setItem(
        "current-report",
        JSON.stringify(newCurrentReprotLS),
      );
      setActiveTab(newCurrentReprotLS.tab);
    }
  }, [id]);

  // --- Workday: caso del denunciante -------------------------------------
  // Una sola denuncia = un solo botón, así que la llave del envío es fija (en
  // Sanciones hay uno por involucrado y ahí la llave es el id de la sanción).
  const WORKDAY_COMPLAINANT_KEY = "complainant";

  const { sendToWorkday: sendWorkdayCase, isSending: isSendingWorkday } =
    useWorkdayCase({ openModalConfirm, closeModalConfirm });
  const getLocalizedLabel = useLocalizedLabel();
  const { severityOptions } = useReportContext();

  // El SOEID del denunciante es un campo del formulario (metadata key "soeid"),
  // así que se busca en los stepers ya clasificados del detalle.
  const complainantSoeid = String(
    (reportDetail?.stepers ?? [])
      .flatMap((step) => step?.form ?? [])
      .find((field) => field?.key === "soeid")?.text_value ?? ""
  ).trim();

  const investigationResponsables = (assignedUsers?.users ?? []).filter((user) =>
    user?.assign_details?.rolesInvestigation?.includes(
      "investigation_responsable",
    ),
  );

  const handleSendComplainantToWorkday = () =>
    sendWorkdayCase({
      key: WORKDAY_COMPLAINANT_KEY,
      soeid: complainantSoeid,
      title: t("Resolution.workday_case_title", {
        folio: reportDetail?.folio ?? "",
      }),
      message: formatWorkdayMessage([
        [t("Resolution.workday_label_report"), reportDetail?.folio],
        [
          t("Resolution.workday_label_classification"),
          getLocalizedLabel(reportDetail?.classification),
        ],
        // Siempre va, aunque no se haya definido
        [
          t("Resolution.workday_label_severity"),
          resolveSeverityLabel({
            severity: reportDetail?.severity,
            severityOptions,
            getLocalizedLabel,
            undefinedLabel: t("Resolution.workday_severity_undefined"),
          }),
        ],
        [t("Resolution.workday_label_complainant"), complainantSoeid],
        [
          t("Resolution.workday_label_responsables", {
            count: investigationResponsables.length,
          }),
          investigationResponsables.map((user) => user.name).join(", "),
        ],
      ]),
    });

  const reportRender = async () => {
    setIsLoading(true);
    try {
      // Por tracking code hay que resolver primero el id del reporte: el detalle
      // acepta id o tc, el resto de los endpoints sólo report_id.
      let resolvedDetail = null;
      let reportId = routeId;
      if (isTrackingCode) {
        setTcReportId(null);
        resolvedDetail = await getReportDetail(routeId);
        if (!resolvedDetail?.id) {
          setIsLoading(false);
          navigate("/reports/all");
          return;
        }
        reportId = String(resolvedDetail.id);
        setTcReportId(reportId);
      }

      const [
        reportDetailApi,
        investigationListApi,
        severityCatApi,
        improperCatApi,
        lockedCatApi,
        relationshipCompany,
        grievanceClassificationCat,
        userListApi,
        offendersListApi,
        victimsListApi,
        advisorReviewApi,
      ] = await Promise.all([
        resolvedDetail ?? getReportDetail(reportId),
        getInvestigationListByReportId(reportId),
        catalogByPart({ catalogue: "RD-102106", is_own: false, path: "/" }),
        catalogByPart({ catalogue: "RD-108101", is_own: false, path: "/" }),
        catalogByPart({ catalogue: "RD-108100", is_own: false, path: "/" }),
        catalogByPart({ catalogue: "RD-102101", is_own: false, path: "/" }),
        catalogByPart({ catalogue: "RC-100", is_own: false, path: "/" }),
        listAssignedUsers(reportId),
        getOffendersListByReportId(reportId),
        getVictimsListByReportId(reportId),
        detailAdvisorReview(reportId),
      ]);

      if (reportDetailApi === null) {
        setIsLoading(false);
        navigate("/reports/all");
      } else {
        setCompanyRelationshipCat(relationshipCompany);
        setReportDetail(reportDetailApi);
        setInvestigationList(investigationListApi);
        setSeverityCat(severityCatApi);
        setImproperCat(improperCatApi);
        setAssignedUsers(userListApi);
        setLockedCat(lockedCatApi);
        setGrievanceClassification(grievanceClassificationCat);
        setDetailReport(reportDetailApi, userListApi);
        setAdvisorReview(advisorReviewApi || {});
        // Recursive function to find objects with `name` and `type` inside arrays
        const findInvolvedObjects = (data) => {
          let involved = [];

          // If data is an array
          if (Array.isArray(data)) {
            data.forEach((item) => {
              involved = [...involved, ...findInvolvedObjects(item)];
            });
          } else if (typeof data === "object" && data !== null) {
            // If the object has the properties `name` and `type`
            if (data.hasOwnProperty("name") && data.hasOwnProperty("type")) {
              involved.push(data);
            }
            // Iterate object properties
            Object.values(data).forEach((value) => {
              involved = [...involved, ...findInvolvedObjects(value)];
            });
          }

          return involved;
        };

        //  Search involved in `form` within `reportDetailApi`.
        const involvedFromForm = findInvolvedObjects(reportDetailApi || []);

        // Search involved in `form` within each item of `stepers`.
        const involvedFromStepers = [];
        for (const item of reportDetailApi?.stepers || []) {
          for (const subItem of item.form) {
            if (subItem.key === "involucrados") {
              involvedFromStepers.push(...subItem.value);
            }
          }
        }

        // Function to check if any form section contains sensitive information
        const findSensitiveKey = () => {
          for (const item of reportDetailApi?.form || []) {
            const jsonSchema = item.form["json-schema"];
            for (const subItem of jsonSchema) {
              if (subItem.key === "involucrados" && subItem.sensitive) {
                return true; // Is sensitive
              }
            }
          }
          return false; // No sensitive
        };

        // Execution of the verification if there is sensitive information
        const sensitive = findSensitiveKey();

        if (!sensitive) {
          // Merge the involved found in `form` and `stepers`.
          const involved = [...involvedFromForm, ...involvedFromStepers];

          if (involved.length > 0) {
            const result = mergerInvolved(involved);

            // Merging reporting and investigation offenders
            if (result.accomplice.length > 0 || result.offender.length > 0) {
              const mergeOffenders = [
                ...(offendersListApi || []),
                ...(result.accomplice || []),
                ...(result.offender || []),
              ].reduce((acc, item) => {
                if (!acc.find((elmnt) => elmnt.name === item.name)) {
                  acc.push(item);
                }
                return acc;
              }, []);
              setOffenders(mergeOffenders.length > 0 ? mergeOffenders : []);
            }

            // Merging reporting and investigation victims
            if (result.victim.length > 0 || result.witness.length > 0) {
              const mergeVictims = [
                ...(victimsListApi || []),
                ...(result.witness || []),
                ...(result.victim || []),
              ].reduce((acc, item) => {
                if (!acc.find((elmnt) => elmnt.name === item.name)) {
                  acc.push(item);
                }
                return acc;
              }, []);
              setVictims(mergeVictims.length > 0 ? mergeVictims : []);
            } else {
              setOffenders(offendersListApi || []);
              setVictims(victimsListApi || []);
            }
            setIsLoading(false);
          } else {
            setOffenders(offendersListApi || []);
            setVictims(victimsListApi || []);
          }
          setIsLoading(false);
        } else {
          setOffenders(offendersListApi || []);
          setVictims(victimsListApi || []);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Merge involved parties
  const mergerInvolved = (involucrados) => {
    const initializeResult = () => ({
      victim: [],
      witness: [],
      offender: [],
      accomplice: [],
    });

    // Adds involved parties and checks duplicates
    const addOrUpdatePerson = (result, person) => {
      const { kind, rol, name, occupation, confirm } = person;

      if (!result[kind]) return;

      const existingIndex = result[kind].findIndex(
        (existingPerson) =>
          existingPerson.name === name &&
          existingPerson.occupation === occupation &&
          existingPerson.kind === kind,
      );

      if (existingIndex !== -1) {
        result[kind][existingIndex].confirm =
          result[kind][existingIndex].confirm || confirm;
      } else {
        result[kind].push({
          rol: rol || "",
          kind: kind || "",
          name: name || "",
          confirm: confirm || true,
          details: "",
          relationship: occupation || "",
          added: true,
        });
      }
    };

    // Eliminates duplicates within each role
    const removeDuplicates = (result) => {
      Object.keys(result).forEach((key) => {
        result[key] = result[key].filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              (otherItem) =>
                item.name === otherItem.name &&
                item.occupation === otherItem.occupation &&
                item.rol === otherItem.rol,
            ),
        );
      });
    };

    const result = initializeResult();
    involucrados.forEach((person) => addOrUpdatePerson(result, person));
    removeDuplicates(result);
    return result;
  };

  // Open PDF modal
  const handlePDFGenerator = () => {
    setShowModalPDFGenerator((showModalPDFGenerator) => !showModalPDFGenerator);
  };

  // Delete report
  const handleDelete = async (reportid) => {
    try {
      await DeleteReport({ reportid }).then((resp) => {
        if (resp.status === "success") {
          navigate("/reports/all");
        } else {
          return;
        }
      });
    } catch (error) {
      console.log("error sending to trash...");
    }
  };

  const returnButtonsHeader2 = () => {
    if (activeTab === "Información") {
      return [
        {
          type: "customIndicators",
          icon: "flag_square",
        },
        ...(reportDetail?.complements?.length > 0
          ? [
              {
                type: "button",
                icon: "outline_document_download",
                label: t("Creator.complements"),
                onClick: () => setShowComplementsModal(true),
              },
            ]
          : []),
        {
          type: "button",
          icon: "outline_document_download",
          label: t("Creator.generate_pdf"),
          onClick: () => setShowModalPDFGenerator(true),
        },
        // Workday del DENUNCIANTE: solo si la denuncia trae su SOEID (las
        // anónimas y las de terceros no lo tienen).
        // ...(complainantSoeid
        //   ? [
        //       {
        //         type: "button",
        //         icon: "send",
        //         label: "Workday",
        //         color: "success",
        //         labelColor: "white",
        //         labelColorHover: "white",
        //         onClick: handleSendComplainantToWorkday,
        //         loading: isSendingWorkday(WORKDAY_COMPLAINANT_KEY),
        //         disabled: isSendingWorkday(WORKDAY_COMPLAINANT_KEY),
        //       },
        //     ]
        //   : []),
        // Editar denuncia deshabilitado temporalmente
        // {
        //   tooltip: t("ReportViewer.edit_form"),
        //   type: "icon-button",
        //   icon: "outline_edit_form",
        //   onClick: () => setEditReport(true),
        // },
        {
          tooltip: t("ReportViewer.reclassification"),
          type: "icon-button",
          icon: "outline_refresh_circle",
          onClick: () => setReclassificationReport(true),
        },
        reportDetail?.status?.current == "draft"
          ? {
              tooltip: t("ReportViewer.trash_form"),
              type: "icon-button",
              icon: "trash_can",
              onClick: () =>
                openModalConfirm({
                  variant: "error",
                  title: t("Resolution.attention"),
                  titleColor: "pink",
                  description1: t(
                    "ReportViewer.you_are_about_delete_this_report",
                  ),
                  question: t("Care.do_you_want_continue"),
                  // Left button
                  leftButtonText: t("Care.delete"),
                  leftButtonVariant: "outlined",
                  leftButtonColor: "error",
                  onClickLeft: () => {
                    handleDelete(reportDetail?.id);
                    closeModalConfirm();
                  },
                  // Right button
                  rightButtonText: t("Care.cancel"),
                  rightButtonVariant: "contained",
                  rightButtonColor: "secondary",
                  onClickRight: closeModalConfirm, // Close modal
                  ethiquin: "megaphone",
                }),
            }
          : [],
      ];
    }
    if (activeTab === "Investigación") {
      return [
        {
          type: "dropdown",
          icon: "bold_flag",
          options: [
            {
              icon: "bold_flag",
              iconColor: "icon",
              label: "Marcador 1",
            },
          ],
        },
      ];
    }
    return [];
  };

  // Sólo izq/der cambian de tab; arriba/abajo quedan para el scroll normal.
  // Se frena en captura para que no llegue al onKeyDown del tablist (react-bootstrap).
  const hdlTabsKeyDown = (e) => {
    if (
      (e.key === "ArrowUp" || e.key === "ArrowDown") &&
      e.target?.getAttribute?.("role") === "tab"
    ) {
      e.stopPropagation();
    }
  };

  const hdlTabChange = (tabKey) => {
    if (
      activeTab === "Investigación" &&
      isLoadingInvestigation &&
      tabKey !== "Investigación"
    ) {
      return;
    }
    setActiveTab(tabKey);
  };

  return (
    <>
      <Confirmation {...modalConfirm} onHide={closeModalConfirm} />
      {isLoading ? (
        <Loading />
      ) : editReport ? (
        <ReportEdit
          reportDetail={reportDetail}
          assignedUsers={assignedUsers}
          setEditReport={setEditReport}
          advisorReview={advisorReview}
        />
      ) : reclassificationReport ? (
        <ReclassificationComponent
          reportDetail={reportDetail}
          setReclassificationReport={setReclassificationReport}
          reloadReport={reportRender}
        />
      ) : (
        <Box>
          {/* Evidence Modal */}
          <ModalAttachments
            files={reportDetail?.attachments}
            open={showModalAttachments}
            onClose={() => setShowModalAttachments(false)}
            fileKey="file"
            no_attachments={t("ReportViewer.no_attachments")}
          />
          {/* PDF Generator Modal */}
          <EthicsModal
            show={showModalPDFGenerator}
            onHide={handlePDFGenerator}
            modalContent={
              <PDFCreator closeModal={handlePDFGenerator} reportId={id} />
            }
            size={"lg"}
          />
          <ReportHeader
            icon="outline_document_text"
            title={`${reportDetail?.folio || t("ReportViewer.no_folio")}`}
            subtitle={`${reportDetail?.tracking_code}`}
            reportDetail={reportDetail}
          />
          <ReportHeader2
            reportDetail={reportDetail}
            assignedUsers={assignedUsers.users}
            buttons={returnButtonsHeader2()}
            contentRef={contentRef}
          />
          <Box className="all-report-tab mt-20" onKeyDownCapture={hdlTabsKeyDown}>
            {/* Dynamic Tabs active={"Menu Tab Selected"} */}
            <Tabs
              activeKey={activeTab}
              onSelect={hdlTabChange}
              className="mb-1 dyTheme1 dyBorder1 rounded d-flex justify-content-evenly"
            >
              <Tab eventKey="Información" title={t("ReportViewer.information")}>
                <Information
                  id={reportDetail.id}
                  investigationList={investigationList}
                  isLoading={isLoading}
                  lockedCat={lockedCat}
                  reportDetail={reportDetail}
                  reportRender={reportRender}
                  setIsLoading={setIsLoading}
                  improperCat={improperCat}
                  severityCat={severityCat}
                  showComplementsModal={showComplementsModal}
                  setShowComplementsModal={setShowComplementsModal}
                  setShowModalAttachments={setShowModalAttachments}
                  advisorReview={advisorReview}
                />
              </Tab>
              <Tab
                eventKey="Status de atención"
                title={t("ReportViewer.status_history")}
              >
                <Care
                  reportDetail={reportDetail}
                  reportRender={reportRender}
                  setActiveTab={setActiveTab}
                  investigationList={investigationList}
                  offenders={offenders}
                  victims={victims}
                />
              </Tab>
              <Tab eventKey="Usuarios" title={t("ReportViewer.assigns_users")}>
                <Users idReport={reportDetail.id} reportDetail={reportDetail} />
              </Tab>
              <Tab
                eventKey="Investigación"
                title={t("ReportViewer.investigation")}
              >
                <InvestigationPage
                  id={id}
                  investigationList={investigationList}
                  reportDetail={reportDetail}
                  onReload={reportRender}
                  companyRelationshipCat={companyRelationshipCat}
                  grievanceClassification={grievanceClassification}
                  showModalPDFGeneratorInvestigation={
                    showModalPDFGeneratorInvestigation
                  }
                  setShowModalPDFGeneratorInvestigation={
                    setShowModalPDFGeneratorInvestigation
                  }
                  showModalPrintInvestigation={showModalPrintInvestigation}
                  setShowModalPrintInvestigation={
                    setShowModalPrintInvestigation
                  }
                  activeTab={activeTab}
                  isLoading={isLoadingInvestigation}
                  setIsLoading={setIsLoadingInvestigation}
                  assignedUsers={assignedUsers.users}
                />
              </Tab>

              <Tab eventKey="Mensajes" title={t("ReportViewer.messages")}>
                <Messages
                  id={reportDetail.id}
                  tracking_code={reportDetail?.tracking_code}
                  reportDetail={reportDetail}
                />
              </Tab>

              {/* mountOnEnter: la bitácora no se monta ni consulta hasta que se
                  entra a la pestaña, para no cargar el arranque del reporte. */}
              <Tab
                eventKey="Bitácora"
                title={t("ReportViewer.bitacora")}
                mountOnEnter
              >
                <ReportBitacoraTab reportId={reportDetail.id} />
              </Tab>
            </Tabs>
          </Box>
        </Box>
      )}
    </>
  );
};
